import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { SectionLoader, EmptyState } from "@/components/common";
import { LocationArt } from "@/components/admin/LocationArt";
import { ordinalWord, locTimes, timeRange, DEFAULT_START } from "@/lib/fridays";
import { MapPin, Plus, Pencil, Trash2, Loader2 } from "lucide-react";

const EMPTY = {
  name: "", host: "", room: "", address: "", city: "", state: "", zip_code: "",
  map_url: "", notes: "", friday_ordinal: "", theme_title: "", theme_tagline: "",
  theme_description: "", parking: "", accent: "blue", directionsText: "", start_time: "12:00", end_time: "15:00",
};

export default function AdminLocations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [artEnabled, setArtEnabled] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get("/locations").then((x) => x.data).catch(() => []);
    r.sort((a, b) => (a.friday_ordinal || 99) - (b.friday_ordinal || 99));
    setItems(r); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/art/status").then((r) => setArtEnabled(!!r.data?.configured)).catch(() => {}); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (l) => {
    setEditing(l);
    setForm({
      ...EMPTY, ...l,
      friday_ordinal: l.friday_ordinal ? String(l.friday_ordinal) : "",
      accent: l.accent || "blue",
      start_time: l.start_time || "12:00",
      end_time: l.end_time || "15:00",
      directionsText: (l.directions || []).join("\n"),
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    setSaving(true);
    const { directionsText, ...rest } = form;
    const payload = {
      ...rest,
      friday_ordinal: form.friday_ordinal ? parseInt(form.friday_ordinal, 10) : null,
      directions: directionsText.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing) await api.put(`/locations/${editing.location_id}`, payload);
      else await api.post("/locations", payload);
      toast.success(editing ? "Location updated" : "Location added");
      setOpen(false); await load();
    } catch (e) { toast.error("Could not save location"); } finally { setSaving(false); }
  };

  const remove = async (l) => {
    try { await api.delete(`/locations/${l.location_id}`); toast.success("Location deleted"); await load(); }
    catch (e) { toast.error("Could not delete"); }
  };

  if (loading) return <SectionLoader />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="font-serif text-2xl font-semibold text-foreground sm:text-3xl">Locations</h1><p className="mt-1 text-muted-foreground">Manage the rotating Friday venues shown on the landing page.</p></div>
        <Button onClick={openCreate} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="admin-location-create-button"><Plus className="mr-2 h-4 w-4" />New location</Button>
      </div>

      {items.length === 0 ? (
        <EmptyState icon={MapPin} title="No locations yet" description="Add a venue to attach to your events." action={<Button onClick={openCreate}><Plus className="mr-2 h-4 w-4" />New location</Button>} />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((l) => (
            <Card key={l.location_id} className="border-border bg-card" data-testid="admin-location-card"><CardContent className="p-5">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2"><MapPin className="mt-0.5 h-5 w-5 text-primary" /><div><p className="font-semibold text-foreground">{l.name}</p><p className="text-sm text-muted-foreground">{[l.address, l.city, l.state].filter(Boolean).join(", ")}</p></div></div>
                {l.friday_ordinal && <Badge variant="secondary" className="shrink-0">{ordinalWord(l.friday_ordinal)} Fri</Badge>}
              </div>
              {l.theme_title && <p className="mt-3 text-sm font-medium text-foreground">{l.theme_title}{l.room ? ` · ${l.room}` : ""}</p>}
              {l.parking && <p className="mt-1 text-xs text-muted-foreground">{l.parking}</p>}
              <p className="mt-1 text-xs text-muted-foreground" data-testid={`admin-location-hours-${l.location_id}`}>Fridays · {timeRange(locTimes(l).start, locTimes(l).end)}{locTimes(l).start !== DEFAULT_START ? " · later start" : ""}</p>
              {artEnabled && <LocationArt loc={l} onChanged={load} />}
              <div className="mt-4 flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => openEdit(l)} data-testid="admin-location-edit"><Pencil className="mr-1.5 h-3.5 w-3.5" />Edit</Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="text-destructive"><Trash2 className="mr-1.5 h-3.5 w-3.5" />Delete</Button></AlertDialogTrigger>
                  <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete location?</AlertDialogTitle><AlertDialogDescription>Events using this location will keep their data but lose the link.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(l)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                </AlertDialog>
              </div>
            </CardContent></Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader><DialogTitle className="font-serif text-xl">{editing ? "Edit location" : "New location"}</DialogTitle></DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label>Venue name</Label><Input className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-location-name-input" /></div>
            <div><Label>Hosted by</Label><Input className="mt-1.5" value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} placeholder="e.g., Hotel Indigo, an IHG Hotel" /></div>
            <div><Label>Room</Label><Input className="mt-1.5" value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} placeholder="e.g., Otis Meeting Room" /></div>
            <div className="sm:col-span-2"><Label>Address</Label><Input className="mt-1.5" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div><Label>City</Label><Input className="mt-1.5" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>State</Label><Input className="mt-1.5" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></div>
              <div><Label>ZIP</Label><Input className="mt-1.5" value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} /></div>
            </div>

            <div><Label>Friday rotation</Label>
              <Select value={form.friday_ordinal || "none"} onValueChange={(v) => setForm({ ...form, friday_ordinal: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1.5" data-testid="admin-location-friday-select"><SelectValue placeholder="Not a Friday venue" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not a Friday venue</SelectItem>
                  <SelectItem value="1">1st Friday</SelectItem>
                  <SelectItem value="2">2nd Friday</SelectItem>
                  <SelectItem value="3">3rd Friday</SelectItem>
                  <SelectItem value="4">4th Friday</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Accent color</Label>
              <Select value={form.accent} onValueChange={(v) => setForm({ ...form, accent: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="blue">Deep Blue</SelectItem>
                  <SelectItem value="copper">Copper</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div><Label>Theme title</Label><Input className="mt-1.5" value={form.theme_title} onChange={(e) => setForm({ ...form, theme_title: e.target.value })} placeholder="e.g., Arts & Entertainment" /></div>
            <div><Label>Theme tagline</Label><Input className="mt-1.5" value={form.theme_tagline} onChange={(e) => setForm({ ...form, theme_tagline: e.target.value })} placeholder="e.g., Creativity. Expression. Inspiration." /></div>
            <div className="sm:col-span-2"><Label>Theme description</Label><Textarea className="mt-1.5" rows={2} value={form.theme_description} onChange={(e) => setForm({ ...form, theme_description: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Directions <span className="text-xs text-muted-foreground">(one step per line)</span></Label><Textarea className="mt-1.5" rows={3} value={form.directionsText} onChange={(e) => setForm({ ...form, directionsText: e.target.value })} placeholder={"Main entrance is on Madison St.\nWatch for Friday Conversations signage."} data-testid="admin-location-directions-input" /></div>
            <div><Label>Start time (Pacific)</Label><Input type="time" className="mt-1.5" value={form.start_time || ""} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="admin-location-start-time" /></div>
            <div><Label>End time (Pacific)</Label><Input type="time" className="mt-1.5" value={form.end_time || ""} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="admin-location-end-time" /></div>
            <div className="sm:col-span-2"><Label>Parking</Label><Input className="mt-1.5" value={form.parking} onChange={(e) => setForm({ ...form, parking: e.target.value })} placeholder="Street parking available." /></div>
            <div className="sm:col-span-2"><Label>Notes <span className="text-xs text-muted-foreground">(internal)</span></Label><Textarea className="mt-1.5" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="admin-location-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save location"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
